import { Component } from '@theme/component';
import { VariantSelectedEvent } from '@theme/events';

/**
 * @typedef {Object} ProductCardVariantsRefs
 * @property {HTMLInputElement[]} variantInputs - Radio inputs for variants (array)
 * @property {HTMLElement | null} quickAdd - The quick-add component
 */

/**
 * Custom element for managing product card variant selection.
 * Handles variant selection and updates the buy button state.
 * @extends {Component<ProductCardVariantsRefs>}
 */
export class ProductCardVariants extends Component {
  requiredRefs = ['variantInputs'];
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {string | null} */
  #selectedVariantId = null;
  /** @type {MutationObserver | null} */
  #buttonObserver = null;

  connectedCallback() {
    super.connectedCallback();
    
    // Wait for refs to be populated
    if (!this.refs.variantInputs || this.refs.variantInputs.length === 0) {
      // Retry after a short delay if refs aren't ready
      setTimeout(() => {
        if (this.refs.variantInputs && this.refs.variantInputs.length > 0) {
          this.#initialize();
        }
      }, 50);
      return;
    }
    
    this.#initialize();
  }
  
  /**
   * Initializes the component after refs are ready.
   */
  #initialize() {
    // Initialize selected variant state
    const scenario = this.dataset.scenario;
    const variantsCount = parseInt(this.dataset.variantsCount || '0', 10);
    
    if (variantsCount === 2 && (scenario === 'two_both_available' || scenario === 'two_one_available')) {
      // Check if any variant is pre-selected
      const checkedInput = Array.from(this.refs.variantInputs || []).find(input => input.checked);
      if (checkedInput && checkedInput.checked) {
        // Variant is pre-selected (for two_one_available scenario)
        this.#selectedVariantId = checkedInput.dataset.variantId;
      } else {
        // No variant selected - clear the variant input
        this.#selectedVariantId = null;
      }
    }
    
    this.#setupEventListeners();
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.#updateButtonState();
      this.#preventFormSubmissionWithoutVariant();
      
      // Also run after a small delay to catch any late-loading elements
      setTimeout(() => {
        this.#updateButtonState();
      }, 100);
    });
  }
  
  /**
   * Prevents form submission when no variant is selected.
   */
  #preventFormSubmissionWithoutVariant() {
    if (!this.#abort) {
      this.#abort = new AbortController();
    }
    
    const productCard = this.closest('product-card');
    if (!productCard) return;
    
    const productForm = productCard.querySelector('product-form-component');
    const form = productForm?.querySelector('form');
    if (!form) return;
    
    const scenario = this.dataset.scenario;
    const variantsCount = parseInt(this.dataset.variantsCount || '0', 10);
    
    // Only for 2 variants scenario
    if (variantsCount === 2 && (scenario === 'two_both_available' || scenario === 'two_one_available')) {
      form.addEventListener('submit', (e) => {
        const variantInput = form.querySelector('input[name="id"]');
        if (!variantInput || !variantInput.value || variantInput.disabled || variantInput.value === '') {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }, { signal: this.#abort.signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#buttonObserver?.disconnect();
    this.#buttonObserver = null;
  }

  /**
   * Sets up event listeners for variant selection.
   */
  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    // Listen to radio button changes
    for (const input of this.refs.variantInputs) {
      input.addEventListener('change', () => this.#handleVariantChange(), opts);
    }

    // Check if a variant is pre-selected (for scenario: two_one_available)
    const preSelected = this.querySelector('[data-default-selected="true"]');
    if (preSelected) {
      const variantId = preSelected.dataset.variantId;
      if (variantId) {
        this.#selectedVariantId = variantId;
        this.#updateButtonState();
      }
    }
    
    // Watch for button being disabled and re-enable if variant is selected
    this.#watchButtonState();
  }
  
  /**
   * Watches the button state and ensures it stays enabled when a variant is selected.
   */
  #watchButtonState() {
    const productCard = this.closest('product-card');
    if (!productCard) return;
    
    const productForm = productCard.querySelector('product-form-component');
    const addButtonContainer = productForm?.querySelector('add-to-cart-component');
    if (!addButtonContainer) return;
    
    // Watch for attribute changes on the button
    const observer = new MutationObserver(() => {
      if (this.#selectedVariantId) {
        const button = addButtonContainer.refs?.addToCartButton;
        if (button instanceof HTMLButtonElement && button.disabled) {
          // Re-enable if variant is selected
          if (typeof addButtonContainer.enable === 'function') {
            addButtonContainer.enable();
          } else {
            button.disabled = false;
            button.removeAttribute('disabled');
          }
        }
      }
    });
    
    const button = addButtonContainer.refs?.addToCartButton;
    if (button) {
      observer.observe(button, {
        attributes: true,
        attributeFilter: ['disabled']
      });
    }
    
    // Store observer to clean up later
    this.#buttonObserver = observer;
  }

  /**
   * Handles variant selection change.
   */
  #handleVariantChange() {
    const checkedInput = Array.from(this.refs.variantInputs || []).find(
      (input) => input.checked && input.dataset.variantId
    );

    if (checkedInput) {
      this.#selectedVariantId = checkedInput.dataset.variantId;
      const isAvailable = checkedInput.dataset.variantAvailable === 'true';
      
        if (isAvailable) {
          this.#updateButtonState();
          // Dispatch VariantSelectedEvent for quick-add component
          this.dispatchEvent(
            new VariantSelectedEvent({ id: this.#selectedVariantId })
          );
        } else {
          // Unavailable variant selected
          this.#selectedVariantId = null;
          this.#updateButtonState();
        }
    } else {
      // No variant selected
      this.#selectedVariantId = null;
      this.#updateButtonState();
    }
  }

  /**
   * Updates the buy button state based on selected variant.
   */
  #updateButtonState() {
    const productCard = this.closest('product-card');
    if (!productCard) return;

    const scenario = this.dataset.scenario;
    const variantsCount = parseInt(this.dataset.variantsCount || '0', 10);

    // For 2 variants scenario
    if (variantsCount === 2 && (scenario === 'two_both_available' || scenario === 'two_one_available')) {
      // Find the form - it's in product-form-component
      const productForm = productCard.querySelector('product-form-component');
      const form = productForm?.querySelector('form');
      const variantInput = form?.querySelector('input[name="id"]') || productForm?.querySelector('input[ref="variantId"]');
      const addButtonContainer = productForm?.querySelector('add-to-cart-component');
      const addButton = addButtonContainer?.querySelector('button[type="submit"]') || productForm?.querySelector('.add-to-cart-button, button[type="submit"]');
      
      // Also check for quick-add-component (for quick-add scenarios)
      const quickAdd = productCard.querySelector('quick-add-component');
      const chooseButton = quickAdd?.querySelector('.quick-add__button--choose');
      
      // Update data-quick-add-button if quick-add exists
      if (quickAdd) {
        if (this.#selectedVariantId) {
          quickAdd.setAttribute('data-quick-add-button', 'add');
        } else {
          quickAdd.setAttribute('data-quick-add-button', 'select_variant');
        }
      }
      
      // Get button text element for regular product cards (not quick-add)
      const buttonTextContent = addButton?.querySelector('.add-to-cart-text__content span span');

      if (this.#selectedVariantId) {
        const selectedInput = Array.from(this.refs.variantInputs).find(
          (input) => input.dataset.variantId === this.#selectedVariantId
        );
        const isAvailable = selectedInput?.dataset.variantAvailable === 'true';

        if (isAvailable) {
          // Variant selected and available - enable button and show "Add to cart"
          
          // Update the variant input in the form - CRITICAL
          if (variantInput) {
            variantInput.value = this.#selectedVariantId;
            variantInput.disabled = false;
            variantInput.removeAttribute('disabled');
            variantInput.setAttribute('value', this.#selectedVariantId);
          }

          // Show and enable the add button
          if (addButtonContainer) {
            addButtonContainer.style.display = 'block';
            
            // Wait a tick to ensure refs are ready
            requestAnimationFrame(() => {
              // Enable via the add-to-cart-component's enable method - this is the proper way
              if (typeof addButtonContainer.enable === 'function') {
                addButtonContainer.enable();
              }
              // Also directly enable the button via refs as fallback
              if (addButtonContainer.refs?.addToCartButton) {
                const buttonViaRefs = addButtonContainer.refs.addToCartButton;
                if (buttonViaRefs instanceof HTMLButtonElement) {
                  buttonViaRefs.disabled = false;
                  buttonViaRefs.removeAttribute('disabled');
                }
              }
            });
          }
          // Also enable the button if found via querySelector (fallback)
          if (addButton instanceof HTMLButtonElement) {
            addButton.disabled = false;
            addButton.removeAttribute('disabled');
            // Restore "Add to cart" text if it was changed
            if (buttonTextContent && addButton.dataset.originalText) {
              buttonTextContent.textContent = addButton.dataset.originalText;
            }
          }
          
          // Double-check after a short delay to ensure button stays enabled
          setTimeout(() => {
            if (addButtonContainer && this.#selectedVariantId) {
              if (typeof addButtonContainer.enable === 'function') {
                addButtonContainer.enable();
              }
              const button = addButtonContainer.refs?.addToCartButton;
              if (button instanceof HTMLButtonElement) {
                button.disabled = false;
                button.removeAttribute('disabled');
              }
            }
          }, 50);

          // Hide the "Select Variant" button (if it exists in quick-add)
          if (chooseButton) {
            chooseButton.style.display = 'none';
          }
        } else {
          // Variant selected but unavailable - show "Select Variant" disabled
          
          // Clear variant input
          if (variantInput) {
            variantInput.value = '';
            variantInput.disabled = true;
          }

          // Disable add button
          if (addButtonContainer) {
            addButtonContainer.style.display = 'block';
          }
          if (addButton instanceof HTMLButtonElement) {
            addButton.disabled = true;
            // Change text to translatable "Choose format"
            if (buttonTextContent && !addButton.dataset.originalText) {
              addButton.dataset.originalText = buttonTextContent.textContent.trim();
            }
            if (buttonTextContent) {
              const chooseFormatText = typeof Theme !== 'undefined' && Theme.translations?.choose_format 
                ? Theme.translations.choose_format 
                : 'Escolher formato';
              buttonTextContent.textContent = chooseFormatText;
            }
          }
          if (chooseButton) {
            chooseButton.style.display = 'grid';
            chooseButton.disabled = true;
          }
        }
      } else {
        // No variant selected - show "Select Variant" disabled
        
        // Clear variant input - CRITICAL: ensure it's empty
        if (variantInput) {
          variantInput.value = '';
          variantInput.disabled = true;
          variantInput.setAttribute('value', '');
        }

        // Disable the add button and change text to "Select Variant"
        if (addButtonContainer) {
          addButtonContainer.style.display = 'block'; // Keep it visible but disabled
        }
        if (addButton instanceof HTMLButtonElement) {
          addButton.disabled = true;
          // Store original text if not already stored
          if (!addButton.dataset.originalText && buttonTextContent) {
            addButton.dataset.originalText = buttonTextContent.textContent.trim();
          }
          // Change button text to translatable "Choose format"
          if (buttonTextContent) { 
            const chooseFormatText = typeof Theme !== 'undefined' && Theme.translations?.choose_format 
              ? Theme.translations.choose_format 
              : 'Escolher formato';
            buttonTextContent.textContent = chooseFormatText;
          }
        }

        // Show the "Select Variant" button (disabled) if it exists in quick-add
        if (chooseButton) {
          chooseButton.style.display = 'grid';
          chooseButton.disabled = true;
        }
      }
    }
  }
}

customElements.define('product-card-variants', ProductCardVariants);
